import { CircularProgress, Stack } from '@mui/material'
import React from 'react'
import { EmptyListState } from '../../ui-kit/EmptyListState'
import { VirtualCursorList } from '../../ui-kit/VirtualCursorList'
import {
  notificationsApi,
  type GetNotificationsQuery,
  type NotificationFeedScope,
  type NotificationResource,
} from '../../../api/notifications'
import { useWindowedCursorList } from '../../../hooks/useWindowedCursorList'
import { CURSOR_LIST_PAGE_SIZE } from '../../../consts/api'
import { EventItem } from '../EventItem'
import { NotificationEventItemBoundary } from '../NotificationEventItemBoundary'

type EventsListProps = {
  scope: NotificationFeedScope
}

export const EventsList = ({ scope }: EventsListProps) => {
  const [loadPageTrigger] = notificationsApi.useLazyGetNotificationsQuery()

  const queryArgs = React.useMemo<GetNotificationsQuery>(
    () => ({
      scope,
      limit: CURSOR_LIST_PAGE_SIZE,
    }),
    [scope],
  )

  const fetchPage = React.useCallback(
    async (cursor: string | null) => {
      const data = await loadPageTrigger(
        {
          ...queryArgs,
          cursor,
        },
        true,
      ).unwrap()
      return {
        items: data.items ?? [],
        nextCursor: data.next_cursor ?? null,
      }
    },
    [loadPageTrigger, queryArgs],
  )

  const {
    items,
    hasMore,
    isInitialLoading,
    isFetchingMore,
    isError,
    loadMore,
  } = useWindowedCursorList<NotificationResource>({
    resetKey: scope,
    fetchPage,
  })

  if (isInitialLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" minHeight={280} width="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (isError && items.length === 0) {
    return <EmptyListState message="Не удалось загрузить события" />
  }

  return (
    <VirtualCursorList
      items={items}
      hasMore={hasMore}
      isLoadingMore={isFetchingMore && hasMore}
      onLoadMore={() => void loadMore()}
      getItemKey={(_index, notification) => notification.id}
      emptyMessage="Нет событий для отображения"
      itemContent={(_index, notification) => (
        <NotificationEventItemBoundary notification={notification}>
          <EventItem notification={notification} />
        </NotificationEventItemBoundary>
      )}
    />
  )
}
